import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ModalSemantic from 'components/modalSemantic';
import SpeedSharpIcon from '@material-ui/icons/SpeedSharp';
import { Button } from 'semantic-ui-react';
import { IWeeklySessionsData } from 'model/v2/providerCapacity';
import { useMutation } from 'react-apollo';
import { UPDATE_PROVIDER_TARGET_SESSIONS } from 'api/graphql/v2/mutations/Providers';
import { ModalContent } from './EditTargetSessionsModal.style';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  providerName: string;
  providerId: number;
  targetSessions: IWeeklySessionsData | null;
  onUpdate: (sessions: IWeeklySessionsData) => void;
}

// AXON-3559: The weekly session limit is edited as a single value. Per-day targets
// are no longer used, so on save we persist the weekly total only and zero the
// per-day fields to keep the stored shape consistent for other consumers.
const MAX_WEEKLY_LIMIT = 999;

const EditTargetSessionsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  providerId,
  targetSessions,
  onUpdate
}: Props) => {
  const [updateTargetSessions] = useMutation(UPDATE_PROVIDER_TARGET_SESSIONS);

  const initialLimit = useMemo(() => targetSessions?.total ?? 0, [
    targetSessions
  ]);

  const [weeklyLimit, setWeeklyLimit] = useState<number>(initialLimit);
  const [displayValue, setDisplayValue] = useState<string>(
    String(initialLimit)
  );
  const [saving, setSaving] = useState(false);

  // Reset state when the modal opens or the limit changes.
  useEffect(() => {
    if (isOpen) {
      setWeeklyLimit(initialLimit);
      setDisplayValue(String(initialLimit));
    }
  }, [isOpen, initialLimit]);

  const hasChanges = useMemo(() => weeklyLimit !== initialLimit, [
    weeklyLimit,
    initialLimit
  ]);

  const handleInputChange = (value: string) => {
    // Allow empty string for clearing input.
    if (value === '') {
      setDisplayValue('');
      return;
    }
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0 || numValue > MAX_WEEKLY_LIMIT) return;
    setDisplayValue(value);
    setWeeklyLimit(numValue);
  };

  const handleInputBlur = () => {
    // On blur, if empty, fall back to 0.
    if (displayValue === '') {
      setDisplayValue('0');
      setWeeklyLimit(0);
    }
  };

  const handleUpdate = useCallback(async () => {
    setSaving(true);
    try {
      const updatedSessions: IWeeklySessionsData = {
        sun: 0,
        mon: 0,
        tues: 0,
        wed: 0,
        thurs: 0,
        fri: 0,
        sat: 0,
        total: weeklyLimit
      };

      await updateTargetSessions({
        variables: {
          data: {
            providerId,
            targetSessions: updatedSessions
          }
        }
      });

      onUpdate(updatedSessions);
      onClose();
    } catch (error) {
      console.error('Error updating weekly session limit:', error);
    } finally {
      setSaving(false);
    }
  }, [weeklyLimit, providerId, updateTargetSessions, onUpdate, onClose]);

  const handleClose = useCallback(() => {
    if (saving) return;
    onClose();
  }, [saving, onClose]);

  return (
    <ModalSemantic
      open={isOpen}
      onClose={handleClose}
      modalWidth={520}
      trigger={<div></div>}
      content={
        <ModalContent>
          <div className="modal-header">
            <h2 className="modal-title">Edit Weekly Session Limit</h2>
            <p className="modal-subtitle">
              Set the maximum recurring sessions this provider should carry per
              week
            </p>
          </div>

          <div className="divider" />

          <div className="section">
            <div className="limit-row">
              <div className="section-header">
                <SpeedSharpIcon className="section-icon" />
                <span className="section-title">Weekly Session Limit</span>
              </div>
              <input
                type="number"
                min="0"
                max={MAX_WEEKLY_LIMIT}
                value={displayValue}
                onChange={e => handleInputChange(e.target.value)}
                onBlur={handleInputBlur}
                className="target-input weekly-limit-input"
              />
            </div>
            <p className="capacity-note">
              Recuuring Smart Scheduling stops recommending this provider once their booked
              recurring sessions reach this limit.
            </p>
          </div>

          <div className="modal-actions">
            <Button basic onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              loading={saving}
              disabled={!hasChanges || saving}
              className={hasChanges ? 'update-btn-active' : 'update-btn'}
            >
              Update
            </Button>
          </div>
        </ModalContent>
      }
    />
  );
};

export default React.memo(EditTargetSessionsModal);
